import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { CookieConsentProvider } from "@/hooks/useCookieConsent";
import { CookieBanner } from "@/components/cookies/CookieBanner";
import { CookieSettings } from "@/components/cookies/CookieSettings";
import { lazy, Suspense } from "react";

// Critical pages - load immediately
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";

// Lazy load all other pages
const Education = lazy(() => import("./pages/Education"));
const Strategy = lazy(() => import("./pages/Strategy"));
const Advisory = lazy(() => import("./pages/Advisory"));
const CustomWorkflows = lazy(() => import("./pages/CustomWorkflows"));
const AboutUs = lazy(() => import("./pages/AboutUs"));
const Team = lazy(() => import("./pages/Team"));
const Services = lazy(() => import("./pages/Services"));
const Expertise = lazy(() => import("./pages/Expertise"));
const Insights = lazy(() => import("./pages/Insights"));
const Contact = lazy(() => import("./pages/Contact"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const AiAdoptionSuccessStories = lazy(() => import("./pages/insights/AiAdoptionSuccessStories"));
const HowSmallLawFirmsAreUsingAi = lazy(() => import("./pages/insights/HowSmallLawFirmsAreUsingAi"));
const EconomicsOfAiInLaw = lazy(() => import("./pages/insights/EconomicsOfAiInLaw"));
const StrategyPackages = lazy(() => import("./pages/strategy/StrategyPackages"));
const ScheduleConsultation = lazy(() => import("./pages/ScheduleConsultation"));
const FreeDiscoverySession = lazy(() => import("./pages/strategy/FreeDiscoverySession"));

// Training pages
const Training = lazy(() => import("./pages/Training"));
const ModulesPricing = lazy(() => import("./pages/training/ModulesPricing"));
const WorkshopsPricing = lazy(() => import("./pages/training/WorkshopsPricing"));
const OneOnOnePricing = lazy(() => import("./pages/training/OneOnOnePricing"));

// Expertise pages
const ContractLifecycle = lazy(() => import("./pages/expertise/ContractLifecycle"));
const CaseManagement = lazy(() => import("./pages/expertise/CaseManagement"));
const DocumentAnalysis = lazy(() => import("./pages/expertise/DocumentAnalysis"));
const Litigation = lazy(() => import("./pages/expertise/Litigation"));
const Compliance = lazy(() => import("./pages/expertise/Compliance"));
const Transactions = lazy(() => import("./pages/expertise/Transactions"));
const ClientManagement = lazy(() => import("./pages/expertise/ClientManagement"));
const PracticeManagement = lazy(() => import("./pages/expertise/PracticeManagement"));
const LegalVector = lazy(() => import("./pages/LegalVector"));

const queryClient = new QueryClient();

// Loading component
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <CookieConsentProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/services" element={<Services />} />
              <Route path="/services/education" element={<Education />} />
              <Route path="/services/strategy" element={<Strategy />} />
              <Route path="/services/strategy/packages" element={<StrategyPackages />} />
              <Route path="/services/strategy/free-discovery-session" element={<FreeDiscoverySession />} />
              <Route path="/services/advisory" element={<Advisory />} />
              <Route path="/services/custom-solutions" element={<CustomWorkflows />} />
              <Route path="/services/training" element={<Training />} />
              <Route path="/services/training/modules-pricing" element={<ModulesPricing />} />
              <Route path="/services/training/workshops-pricing" element={<WorkshopsPricing />} />
              <Route path="/services/training/1-1-pricing" element={<OneOnOnePricing />} />
              <Route path="/expertise" element={<Expertise />} />
              <Route path="/about-us" element={<AboutUs />} />
              <Route path="/team" element={<Team />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/schedule-consultation" element={<ScheduleConsultation />} />
              
              {/* Insights Routes */}
              <Route path="/insights" element={<Insights />} />
              <Route path="/insights/ai-adoption-in-legal-work-success-stories" element={<AiAdoptionSuccessStories />} />
              <Route path="/insights/how-small-law-firms-are-using-ai" element={<HowSmallLawFirmsAreUsingAi />} />
              <Route path="/insights/economics-of-ai-in-law" element={<EconomicsOfAiInLaw />} />
              
              {/* Expertise Routes */}
              <Route path="/expertise/contract-lifecycle" element={<ContractLifecycle />} />
              <Route path="/expertise/case-management" element={<CaseManagement />} />
              <Route path="/expertise/document-analysis" element={<DocumentAnalysis />} />
              <Route path="/expertise/litigation" element={<Litigation />} />
              <Route path="/expertise/compliance" element={<Compliance />} />
              <Route path="/expertise/transactions" element={<Transactions />} />
              <Route path="/expertise/client-management" element={<ClientManagement />} />
              <Route path="/expertise/practice-management" element={<PracticeManagement />} />
              
              {/* LegalVector Demo Route */}
              <Route path="/legalvector" element={<LegalVector />} />
              
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          <CookieBanner />
          <CookieSettings />
        </BrowserRouter>
      </TooltipProvider>
    </CookieConsentProvider>
  </QueryClientProvider>
);

export default App;
